import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatMinor } from "@/lib/CurrencyFormatter";

/**
 * Iter XX.44.J — Founder Command Center.
 * One-page morning briefing. Read-only. No fabricated numbers.
 * If a counter is null, we render "No data yet." verbatim.
 */
export default function FounderCommandCenter() {
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/command-center");
      setSnap(r.data);
      setError(null);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403) setError("auth");
      else setError("network");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function refresh() {
    load();
    toast.success("Refreshed");
  }

  if (loading && !snap) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">
        <div className="animate-pulse text-neutral-400">Loading command center…</div>
      </div>
    );
  }
  if (error === "auth") {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6" data-testid="fcc-auth-required">
        <div className="max-w-md text-center">
          <div className="text-2xl font-semibold mb-2">Founder only</div>
          <div className="text-neutral-400 mb-4">Sign in as the admin to view the Command Center.</div>
          <Button onClick={() => navigate("/login?return_to=/admin/command")} className="bg-emerald-500 text-neutral-950">Sign in</Button>
        </div>
      </div>
    );
  }
  if (!snap) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">
        <div className="text-neutral-400">{"Couldn't load command center. Please refresh."}</div>
      </div>
    );
  }

  const y = snap.yesterday || {};
  const f = snap.failures || {};
  const ph = snap.product_health || {};
  const bl = snap.biggest_growth_blocker || {};

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="fcc-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <Link to="/admin" className="text-xs text-neutral-500 hover:text-neutral-300" data-testid="fcc-back">← Admin</Link>
            <h1 className="text-3xl sm:text-4xl font-semibold mt-1 font-display">Command Center</h1>
            <p className="text-xs text-neutral-500 mt-1">
              Generated {new Date(snap.generated_at).toLocaleString()} · UTC windows · every counter is real.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <HealthPill status={ph.value} />
            <Button variant="outline" size="sm" onClick={refresh} className="border-neutral-700" data-testid="fcc-refresh">Refresh</Button>
          </div>
        </div>

        {/* Biggest growth blocker — top of the page */}
        <BlockerCard blocker={bl} onAction={() => bl.action_path && navigate(bl.action_path)} />

        {/* Iter XX.70.2 — Founder Preview control panel. Admin-only.
            Never touches billing. Every flag toggle here is scoped to
            the XX.70 preview cluster; the backend enforces this via
            _XX70_TOGGLEABLE_FLAGS. Use this instead of DevTools. */}
        <FounderPreviewPanel />

        {/* Yesterday grid */}
        <h2 className="text-xs uppercase tracking-wider text-neutral-500 mt-8 mb-3">Yesterday (UTC)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="New players" value={y.new_players?.value} testid="fcc-new-players" />
          <MetricCard label="Active players" value={y.active_players?.value} testid="fcc-active-players" />
          <MetricCard label="New partners" value={y.new_partners?.value} testid="fcc-new-partners" />
          <MetricCard label="Active partners (7d)" value={y.active_partners?.value} testid="fcc-active-partners" />
          <MetricCard label="Revenue" value={y.revenue?.value} unit={y.revenue?.unit || "USD"} testid="fcc-revenue" money />
          <MetricCard label="New Plus subs" value={y.plus_subs?.value} testid="fcc-plus-subs" extra={y.plus_subs?.total_active != null ? `${y.plus_subs.total_active} active total` : null} />
          <MetricCard label="Partner referrals" value={y.partner_referrals?.value} testid="fcc-referrals" />
          <MetricCard label="Top acquisition (7d)" value={y.top_acquisition?.value} testid="fcc-top-acq" extra={y.top_acquisition?.count ? `${y.top_acquisition.count} clicks` : null} isHandle />
        </div>

        {/* Action queues */}
        <h2 className="text-xs uppercase tracking-wider text-neutral-500 mt-8 mb-3">Action queues</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ActionCard
            label="Payouts pending"
            value={y.payouts_pending?.value}
            body={y.payouts_pending?.amount_paise ? `${formatMinor(y.payouts_pending.amount_paise, { currency: "INR" })} across all requests` : null}
            cta="Process payouts"
            onClick={() => navigate("/admin")}
            testid="fcc-payouts"
          />
          <ActionCard
            label="Growth Agent reviews"
            value={y.growth_agent_reviews?.value}
            body="Inbound replies waiting for you"
            cta="Open Growth Agent"
            onClick={() => navigate("/admin")}
            testid="fcc-growth-reviews"
          />
        </div>

        {/* Failures */}
        <h2 className="text-xs uppercase tracking-wider text-neutral-500 mt-8 mb-3">Failures (last 24h)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fcc-failures">
          <FailureCard label="Background jobs" value={f.background_jobs?.value} testid="fcc-fail-bg" />
          <FailureCard label="Email sends" value={f.email_sends?.value} testid="fcc-fail-email" />
          <FailureCard label="Video jobs" value={f.video_jobs?.value} testid="fcc-fail-video" />
          <FailureCard label="Stripe webhooks" value={f.stripe_webhooks?.value} testid="fcc-fail-stripe" />
        </div>

        {/* Commitment tier distribution — Iter XX.51 */}
        {snap.commitment_distribution && (
          <div className="mt-8" data-testid="fcc-commitment">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs uppercase tracking-wider text-neutral-500">Commitment tiers (active users, 14d)</h2>
              <Link to="/admin/story-moments"
                    className="text-xs text-emerald-400 hover:text-emerald-300"
                    data-testid="fcc-story-moments-link">
                Story Moments · shadow →
              </Link>
            </div>
            <div className="rounded-xl border border-neutral-800 bg-neutral-950 p-4">
              <div className="text-xs text-neutral-500 mb-3">
                Behavioural tiers computed from real signals (returns, videos, patterns, references, recommendations). LOW = never mention Plus; MEDIUM = mention only when the message intersects Plus value; HIGH = explain Plus only on direct need.
              </div>
              <div className="grid grid-cols-3 gap-3">
                <TierCard label="LOW"    value={snap.commitment_distribution.counts?.LOW ?? 0}    tone="neutral" testid="fcc-tier-low" />
                <TierCard label="MEDIUM" value={snap.commitment_distribution.counts?.MEDIUM ?? 0} tone="warm"    testid="fcc-tier-medium" />
                <TierCard label="HIGH"   value={snap.commitment_distribution.counts?.HIGH ?? 0}   tone="hot"     testid="fcc-tier-high" />
              </div>
              <div className="text-xs text-neutral-600 mt-3">
                {snap.commitment_distribution.active_users ?? 0} active users in the last 14 days.
              </div>
            </div>
          </div>
        )}

        {/* Iter XX.55 — Founder Dot-Plot User Export */}
        <DotPlotExportCard />

        {/* Unknown product questions — Iter XX.49 feedback loop */}
        {snap.unknown_product_questions?.value > 0 && (
          <div className="mt-8" data-testid="fcc-unknown-qs">
            <h2 className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Product questions Gully didn&apos;t know</h2>
            <div className="rounded-xl border border-amber-800/40 bg-amber-950/20 p-4">
              <div className="text-xs text-neutral-400 mb-3">
                Each row is a candidate for `backend/product_knowledge.py`. Adding it teaches Gully permanently.
              </div>
              <div className="space-y-2">
                {(snap.unknown_product_questions.top || []).map((q, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 text-sm py-2 border-b border-amber-900/30 last:border-0" data-testid={`fcc-unknown-q-${i}`}>
                    <div className="text-neutral-200 flex-1">{q.question}</div>
                    <div className="text-xs text-amber-400 font-medium whitespace-nowrap">
                      asked {q.count}×
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Debug / grounded note */}
        <div className="text-xs text-neutral-600 mt-10 text-center" data-testid="fcc-grounded">
          {snap.grounded_note}
        </div>
      </div>
    </div>
  );
}

function HealthPill({ status }) {
  const map = {
    green:            { text: "Product health: green",            cls: "bg-emerald-950/60 border-emerald-800/60 text-emerald-200" },
    green_with_gaps:  { text: "Product health: green (partial)",  cls: "bg-emerald-950/40 border-emerald-800/40 text-emerald-300" },
    amber:            { text: "Product health: amber",            cls: "bg-amber-950/60 border-amber-800/60 text-amber-200" },
    red:              { text: "Product health: red",              cls: "bg-red-950/60 border-red-800/60 text-red-200" },
  };
  const m = map[status] || { text: "Product health: unknown", cls: "bg-neutral-900 border-neutral-800 text-neutral-300" };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs ${m.cls}`} data-testid="fcc-health-pill">
      <span className="w-2 h-2 rounded-full bg-current inline-block" />
      {m.text}
    </div>
  );
}

function MetricCard({ label, value, unit, testid, money, extra, isHandle }) {
  const showNoData = value === null || value === undefined;
  let display;
  if (showNoData) {
    display = <span className="text-neutral-500 text-lg">No data yet</span>;
  } else if (money && typeof value === "number") {
    display = (
      <span className="text-3xl font-semibold text-emerald-300">
        {unit === "USD" ? "$" : ""}{Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </span>
    );
  } else if (isHandle && typeof value === "string") {
    display = <span className="text-2xl font-semibold text-emerald-300 truncate block">@{value}</span>;
  } else {
    display = <span className="text-3xl font-semibold text-emerald-300">{typeof value === "number" ? Number(value).toLocaleString() : String(value)}</span>;
  }
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4" data-testid={testid}>
      {display}
      <div className="text-xs text-neutral-500 mt-1">{label}</div>
      {extra && !showNoData && <div className="text-[10px] text-neutral-600 mt-1">{extra}</div>}
    </div>
  );
}

function ActionCard({ label, value, body, cta, onClick, testid }) {
  const v = value ?? null;
  const zero = (v === null || v === 0);
  return (
    <div className={`border rounded-lg p-4 ${zero ? "bg-neutral-900 border-neutral-800" : "bg-amber-950/40 border-amber-800/50"}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs text-neutral-400">{label}</div>
          <div className={`text-3xl font-semibold ${zero ? "text-neutral-500" : "text-amber-200"}`}>
            {v === null ? "No data yet" : v}
          </div>
        </div>
        {!zero && (
          <Button size="sm" onClick={onClick} className="bg-amber-500 hover:bg-amber-400 text-neutral-950" data-testid={`${testid}-cta`}>
            {cta} →
          </Button>
        )}
      </div>
      {body && !zero && <div className="text-xs text-neutral-400 mt-1">{body}</div>}
    </div>
  );
}

function TierCard({ label, value, tone, testid }) {
  const cls = tone === "hot"    ? "bg-emerald-950/30 border-emerald-800/40 text-emerald-200"
            : tone === "warm"  ? "bg-amber-950/20 border-amber-800/30 text-amber-200"
            :                     "bg-neutral-900 border-neutral-800 text-neutral-300";
  return (
    <div className={`rounded-lg border p-3 ${cls}`} data-testid={testid}>
      <div className="text-2xl font-semibold">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1 opacity-70">{label}</div>
    </div>
  );
}


function FailureCard({ label, value, testid }) {
  const zero = value === 0 || value === null || value === undefined;
  const hasFailures = typeof value === "number" && value > 0;
  return (
    <div className={`border rounded-lg p-3 ${hasFailures ? "bg-red-950/40 border-red-800/50" : "bg-neutral-900 border-neutral-800"}`} data-testid={testid}>
      <div className={`text-2xl font-semibold ${hasFailures ? "text-red-300" : "text-neutral-500"}`}>
        {value === null || value === undefined ? "—" : value}
      </div>
      <div className="text-xs text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

function BlockerCard({ blocker, onAction }) {
  if (!blocker || !blocker.value) return null;
  const isNoBlocker = blocker.value === "no_urgent_blocker";
  return (
    <div
      className={`rounded-xl p-5 border ${isNoBlocker ? "bg-emerald-950/40 border-emerald-800/60" : "bg-amber-950/50 border-amber-700/60"}`}
      data-testid="fcc-blocker"
    >
      <div className="text-xs uppercase tracking-wider text-neutral-400 mb-1">
        {isNoBlocker ? "Today's read" : "Biggest growth blocker"}
      </div>
      <div className={`text-lg font-medium ${isNoBlocker ? "text-emerald-200" : "text-amber-200"}`} data-testid="fcc-blocker-code">
        {blocker.value.replace(/_/g, " ")}
      </div>
      {blocker.detail && (
        <div className="text-sm text-neutral-300 mt-2 leading-relaxed" data-testid="fcc-blocker-detail">
          {blocker.detail}
        </div>
      )}
      {blocker.action_path && !isNoBlocker && (
        <Button size="sm" onClick={onAction} className="mt-3 bg-amber-500 hover:bg-amber-400 text-neutral-950" data-testid="fcc-blocker-cta">
          Go handle this →
        </Button>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter XX.55 — Founder Dot-Plot User Export card.
//
// Two buttons:
//   * "Download User Dot-Plot Export" → GET /api/admin/founder/dot-plot-export
//     (returns a ZIP; filename preserved from Content-Disposition).
//   * "View Export Summary" → GET /api/admin/founder/dot-plot-export/summary
//     (renders the totals + missing-value counts inline).
//
// The bearer JWT is attached automatically by the shared axios interceptor
// in `src/lib/api.js`. It is never rendered in the DOM or logged.
// ---------------------------------------------------------------------------
function DotPlotExportCard() {
  const [downloading, setDownloading] = useState(false);
  const [downloadStage, setDownloadStage] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [lastDownload, setLastDownload] = useState(null);

  function formatError(e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail
      || e?.response?.statusText
      || e?.message
      || "Unknown error";
    if (status === 401) return "401 · Not signed in. Sign in as the admin and retry.";
    if (status === 403) return "403 · This account is not a founder / admin.";
    if (status === 404) {
      // Distinguish resource-404 (job gone) from route-404 (endpoint missing).
      const url = e?.config?.url || "";
      if (url.includes("/jobs/")) return "404 · That export job expired or was cleaned up. Click Download again to start a new one.";
      return "404 · Endpoint missing on this deploy. Redeploy Iter XX.55 to production.";
    }
    if (status === 409) return `409 · ${detail}`;
    if (status >= 500) return `${status} · Server error: ${detail}`;
    if (status) return `${status} · ${detail}`;
    return `Network error: ${detail}`;
  }

  function extractFilename(headers, fallback) {
    const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
    // RFC 5987 (filename*=UTF-8''…) or plain filename="…"
    const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
    if (star) { try { return decodeURIComponent(star[1]); } catch { /* fall through */ } }
    const plain = /filename="?([^";]+)"?/i.exec(cd);
    if (plain) return plain[1];
    return fallback;
  }

  async function download() {
    setDownloading(true);
    setDownloadError(null);
    setDownloadStage("Queuing export…");
    try {
      // Iter XX.55.D — use the async job path so the ZIP never rides an
      // open HTTP request through the proxy timeout.
      const start = await api.post("/admin/founder/dot-plot-export/jobs");
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("no job_id returned");

      // Poll status. Each poll is a tiny JSON call so nothing times out.
      // If a poll returns 404 mid-run (pod restart / TTL / etc.) we
      // treat it as a lost job and restart automatically once, so the
      // founder isn't stranded with a confusing error.
      const deadline = Date.now() + 15 * 60 * 1000; // 15 minutes hard cap
      let job;
      let restartsUsed = 0;
      let activeJobId = jobId;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 2000));
        let s;
        try {
          s = await api.get(`/admin/founder/dot-plot-export/jobs/${activeJobId}`);
        } catch (pollErr) {
          if (pollErr?.response?.status === 404 && restartsUsed < 1) {
            restartsUsed += 1;
            setDownloadStage("Job lost — restarting…");
            const restart = await api.post("/admin/founder/dot-plot-export/jobs");
            activeJobId = restart.data?.job_id;
            if (!activeJobId) throw new Error("could not restart job");
            continue;
          }
          throw pollErr;
        }
        job = s.data;
        if (job.status === "completed") break;
        if (job.status === "failed") throw new Error(job.error || "job failed");
        const stage = job.progress?.stage || job.status;
        const total = job.progress?.total || 0;
        const done  = job.progress?.done  || 0;
        setDownloadStage(
          total > 0 && stage === "commitment"
            ? `Scoring commitment ${done}/${total}…`
            : `Preparing export · ${stage}${job.elapsed_s ? ` · ${job.elapsed_s}s` : ""}`
        );
      }
      if (!job || job.status !== "completed") throw new Error("export timed out");

      // Job done — download the cached ZIP.
      setDownloadStage("Downloading ZIP…");
      const r = await api.get(
        `/admin/founder/dot-plot-export/jobs/${activeJobId}/download`,
        { responseType: "blob" },
      );
      const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\..+/, "Z");
      const fallback = `gully_dot_plot_export_${stamp}.zip`;
      const filename = extractFilename(r.headers, fallback);
      const rowCount = r.headers["x-row-count"] || r.headers["X-Row-Count"];
      const generatedAt = r.headers["x-generated-at"] || r.headers["X-Generated-At"];
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setLastDownload({
        filename,
        rowCount,
        generatedAt,
        stageTimings: job.stage_timings || {},
      });
      toast.success("Export downloaded");
    } catch (e) {
      let msg = formatError(e);
      const data = e?.response?.data;
      if (data && typeof data.text === "function") {
        try {
          const txt = await data.text();
          try {
            const j = JSON.parse(txt);
            if (j?.detail) msg = `${e?.response?.status || ""} · ${j.detail}`.trim();
          } catch { /* not JSON */ }
        } catch { /* ignore */ }
      } else if (e?.message && !e?.response) {
        msg = e.message;
      }
      setDownloadError(msg);
      toast.error(msg);
    } finally {
      setDownloading(false);
      setDownloadStage("");
    }
  }

  async function loadSummary() {
    setSummaryLoading(true);
    setSummaryError(null);
    setSummary(null);
    try {
      const r = await api.get("/admin/founder/dot-plot-export/summary");
      setSummary(r.data);
    } catch (e) {
      setSummaryError(formatError(e));
    } finally {
      setSummaryLoading(false);
    }
  }

  return (
    <div className="mt-8" data-testid="fcc-dot-plot-export">
      <h2 className="text-xs uppercase tracking-wider text-neutral-500 mb-3">User dot-plot export (Iter XX.55)</h2>
      <div className="rounded-xl border border-neutral-800 bg-neutral-950 p-5">
        <div className="text-sm text-neutral-300 leading-relaxed mb-4">
          One row per real user. Anonymised, no PII, missing data left blank.
          Bundle includes <span className="text-neutral-100">users.csv</span>,
          <span className="text-neutral-100"> data_dictionary.csv</span>,
          <span className="text-neutral-100"> data_quality.csv</span> and
          <span className="text-neutral-100"> README.txt</span>.
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={download}
            disabled={downloading}
            className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950 disabled:opacity-60"
            data-testid="fcc-export-download-btn"
          >
            {downloading ? (downloadStage || "Preparing export…") : "Download User Dot-Plot Export"}
          </Button>
          <Button
            onClick={loadSummary}
            variant="outline"
            disabled={summaryLoading}
            className="border-neutral-700 disabled:opacity-60"
            data-testid="fcc-export-summary-btn"
          >
            {summaryLoading ? "Loading…" : "View Export Summary"}
          </Button>
        </div>

        {downloadError && (
          <div
            className="mt-3 text-sm text-red-300 bg-red-950/40 border border-red-800/50 rounded-lg px-3 py-2"
            data-testid="fcc-export-download-error"
          >
            {downloadError}
          </div>
        )}

        {lastDownload && !downloadError && (
          <div className="mt-3 text-xs text-neutral-400" data-testid="fcc-export-download-meta">
            Last download: <span className="text-neutral-200">{lastDownload.filename}</span>
            {lastDownload.rowCount != null && <> · <span className="text-neutral-200">{lastDownload.rowCount}</span> rows</>}
            {lastDownload.generatedAt && <> · generated {new Date(lastDownload.generatedAt).toLocaleString()}</>}
          </div>
        )}

        {summaryError && (
          <div
            className="mt-4 text-sm text-red-300 bg-red-950/40 border border-red-800/50 rounded-lg px-3 py-2"
            data-testid="fcc-export-summary-error"
          >
            {summaryError}
          </div>
        )}

        {summary && !summaryError && (
          <div className="mt-4 rounded-lg border border-neutral-800 bg-neutral-900 p-4" data-testid="fcc-export-summary">
            <div className="text-xs text-neutral-500 mb-2">
              Generated {new Date(summary.generated_at).toLocaleString()} · salt {summary.salt_hash}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <SummaryStat label="Users included" value={summary.row_count} tone="ok" testid="fcc-export-summary-included" />
              <SummaryStat label="Users seen"      value={summary.totals?.users_seen} testid="fcc-export-summary-seen" />
              <SummaryStat label="Founder / admin excluded" value={summary.totals?.users_excluded_founder_or_admin} testid="fcc-export-summary-founder-excluded" />
              <SummaryStat label="QA / test excluded"       value={summary.totals?.users_excluded_test_or_qa} testid="fcc-export-summary-qa-excluded" />
              <SummaryStat label="No-activity excluded"      value={summary.totals?.users_excluded_no_activity} testid="fcc-export-summary-noact-excluded" />
              <SummaryStat label="Duplicates merged"          value={summary.totals?.duplicate_identities_merged} testid="fcc-export-summary-dupes" />
            </div>
            {summary.missing_by_column && summary.fast_path !== true && (
              <details className="mt-3 text-xs text-neutral-400">
                <summary className="cursor-pointer text-neutral-300" data-testid="fcc-export-summary-missing-toggle">
                  Missing-value counts by column
                </summary>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" data-testid="fcc-export-summary-missing">
                  {Object.entries(summary.missing_by_column).map(([col, n]) => (
                    <div key={col} className="flex items-center justify-between border-b border-neutral-800 py-1">
                      <span className="text-neutral-400">{col}</span>
                      <span className={n > 0 ? "text-amber-300" : "text-neutral-600"}>{n}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
            {summary.fast_path && (
              <div className="mt-3 text-[11px] text-neutral-500" data-testid="fcc-export-summary-fastpath">
                Fast-path summary — per-column missing-value counts are inside the ZIP&apos;s <span className="text-neutral-300">data_quality.csv</span>.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, tone, testid }) {
  const cls = tone === "ok"
    ? "bg-emerald-950/40 border-emerald-800/40 text-emerald-200"
    : "bg-neutral-950 border-neutral-800 text-neutral-200";
  return (
    <div className={`rounded-lg border p-3 ${cls}`} data-testid={testid}>
      <div className="text-2xl font-semibold">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">{label}</div>
    </div>
  );
}


// ============================================================
// Iter XX.70.2 — Founder Preview control panel.
// Admin-only. Only the 4 XX.70-preview flags are exposed here so a
// misclick can never affect anything outside the reveal experience.
// ============================================================
const XX70_FLAGS = [
  { key: "founder_preview_enabled",       label: "Founder preview mode",
    hint: "Enables ?founder_preview=1 on chat and admin listing endpoint." },
  { key: "locked_insights_enabled",       label: "Locked cards in review",
    hint: "Free users see rank-1 revealed + locked cards for ranks 2+." },
  { key: "stripe_video_upgrade_enabled",  label: "Stripe upgrade from review",
    hint: "Enables the /checkout endpoint. Leave OFF for the Simulate button flow." },
  { key: "seamless_continuation_enabled", label: "Natural continuation after reveal",
    hint: "Gully sends a synthesis message once cards animate open." },
];

function FounderPreviewPanel() {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/admin/xx68-flags");
      setFlags(data.flags || {});
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load flag state.");
    }
  }
  useEffect(() => { load(); }, []);

  async function toggle(flag, next) {
    setSaving(flag); setError(null);
    try {
      await api.post("/admin/xx68-flags", { flag, enabled: next });
      await load();
      toast.success(`${flag} → ${next ? "ON" : "OFF"}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update flag.");
    } finally {
      setSaving(null);
    }
  }

  async function resetAll() {
    setSaving("__all__"); setError(null);
    try {
      for (const f of XX70_FLAGS) {
        await api.post("/admin/xx68-flags", { flag: f.key, enabled: false });
      }
      await load();
      toast.success("All XX.70 flags reset to OFF");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to reset flags.");
    } finally {
      setSaving(null);
    }
  }

  const allOn = flags && XX70_FLAGS.slice(0, 3).every(
    (f) => flags[f.key]?.global === true,
  );
  // Reset-My-Plus is only visible when the preview flag is globally
  // ON. The backend enforces the same guard — the UI reads the state
  // just to avoid showing a button that would 403 anyway.
  const previewFlagOn = flags?.["founder_preview_enabled"]?.global === true;

  async function runResetMyPlus() {
    setSaving("__reset_plus__"); setError(null); setResetResult(null);
    try {
      const { data } = await api.post(
        "/admin/founder-preview/reset-my-plus", {},
      );
      setResetResult(data);
      toast.success("Your local Plus state was cleared. Stripe untouched.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "reset_failed";
      setError(
        detail === "founder_preview_disabled"
          ? "Turn ON founder_preview_enabled before resetting."
          : `Reset failed: ${detail}`,
      );
    } finally {
      setSaving(null);
      setConfirmReset(false);
    }
  }

  return (
    <div
      data-testid="fcc-founder-preview-panel"
      className="mt-6 rounded-xl border border-amber-900/50 bg-amber-950/20 p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-400">
            Founder Preview · Iter XX.70.1
          </div>
          <div className="text-lg font-semibold text-amber-100 mt-0.5">
            First Paid Wow — flag control
          </div>
        </div>
        <button
          type="button"
          data-testid="fcc-founder-preview-reset"
          onClick={resetAll}
          disabled={saving === "__all__"}
          className="text-xs rounded-md border border-amber-800/50 px-2.5 py-1 text-amber-300 hover:bg-amber-900/30 disabled:opacity-50"
        >
          {saving === "__all__" ? "Resetting…" : "Reset all to OFF"}
        </button>
      </div>

      {flags == null ? (
        <div className="text-xs text-amber-300/80">Loading flag state…</div>
      ) : (
        <div className="space-y-2">
          {XX70_FLAGS.map((f) => {
            const on = flags[f.key]?.global === true;
            return (
              <div key={f.key}
                   className="flex items-start justify-between gap-3 rounded-md border border-amber-900/30 bg-neutral-950/40 p-3"
                   data-testid={`fcc-preview-flag-row-${f.key}`}>
                <div className="min-w-0">
                  <div className="text-sm text-neutral-100">{f.label}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">{f.hint}</div>
                  <div className="text-[10px] text-neutral-600 mt-1 font-mono">{f.key}</div>
                </div>
                <button
                  type="button"
                  data-testid={`fcc-preview-flag-toggle-${f.key}`}
                  onClick={() => toggle(f.key, !on)}
                  disabled={saving === f.key}
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    on
                      ? "bg-emerald-600 text-white hover:bg-emerald-500"
                      : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                  } disabled:opacity-50`}
                >
                  {saving === f.key ? "…" : (on ? "ON" : "OFF")}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <div className="mt-3 text-xs text-red-400" data-testid="fcc-preview-flag-error">{error}</div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to={`/chat?founder_preview=1`}
          data-testid="fcc-preview-open-chat"
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            allOn
              ? "bg-amber-500 text-neutral-950 hover:bg-amber-400"
              : "bg-neutral-800 text-neutral-500 pointer-events-none"
          }`}
        >
          Open founder-preview chat →
        </Link>
        <span className="text-[11px] text-neutral-500 self-center">
          Enable the first three flags to launch Path A.
        </span>
      </div>

      {/* Iter XX.70.2 — Reset My Plus (preview utility). Admin-only,
          founder_preview_enabled-gated on the server. Only clears the
          caller's OWN local billing + wow-moment state — never calls
          Stripe, never touches other users. */}
      {previewFlagOn && (
        <div
          data-testid="fcc-reset-my-plus-block"
          className="mt-4 pt-4 border-t border-amber-900/40"
        >
          <div className="text-[11px] text-amber-300/80 mb-2 leading-relaxed">
            <strong>Preview / test-mode only.</strong> Clears your local Plus
            state so you can replay the reveal + Stripe test-mode journey.
            Does not cancel any Stripe subscription — if you&apos;re actually
            paying, cancel in the Stripe dashboard instead.
          </div>
          <button
            type="button"
            data-testid="fcc-reset-my-plus-button"
            onClick={() => setConfirmReset(true)}
            disabled={saving === "__reset_plus__"}
            className="rounded-md border border-red-900/60 bg-red-950/40 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-950/70 disabled:opacity-50"
          >
            {saving === "__reset_plus__" ? "Resetting…" : "Reset my Plus for another test"}
          </button>
          {resetResult && (
            <div
              data-testid="fcc-reset-my-plus-result"
              className="mt-3 rounded-md border border-emerald-900/40 bg-emerald-950/30 px-3 py-2 text-[11px] text-emerald-200"
            >
              Reset complete at {new Date(resetResult.reset_at).toLocaleTimeString()}.
              Cleared events: {Object.entries(resetResult.dedupe_events_cleared || {})
                .filter(([_, n]) => n > 0)
                .map(([k, n]) => `${k}(${n})`).join(", ") || "none"}.
              Stripe untouched.
            </div>
          )}
        </div>
      )}

      {/* Confirmation dialog for Reset My Plus */}
      {confirmReset && (
        <div
          role="dialog"
          data-testid="fcc-reset-my-plus-confirm"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setConfirmReset(false)}
            aria-hidden
          />
          <div className="relative w-full max-w-md rounded-xl border border-neutral-800 bg-neutral-950 p-5 shadow-2xl">
            <div className="text-sm font-semibold text-amber-200">
              Reset your local Plus state?
            </div>
            <div className="mt-2 text-[12px] text-neutral-400 leading-relaxed space-y-2">
              <p>
                This clears <strong>your own</strong> Plus activation, the
                first-paid-wow marker, and the reveal-funnel dedupe events —
                so you can replay the paid journey.
              </p>
              <p>
                <strong className="text-red-300">
                  It does NOT call Stripe.
                </strong>{" "}
                If you hold a real subscription, it remains active in Stripe
                and can re-activate on the next webhook. Cancel real
                subscriptions in the Stripe dashboard.
              </p>
              <p>
                Uploaded videos, conversations, and insights are preserved.
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                data-testid="fcc-reset-my-plus-cancel"
                onClick={() => setConfirmReset(false)}
                className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs text-neutral-300 hover:bg-neutral-900"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="fcc-reset-my-plus-confirm-yes"
                onClick={runResetMyPlus}
                disabled={saving === "__reset_plus__"}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-500 disabled:opacity-50"
              >
                {saving === "__reset_plus__" ? "Resetting…" : "Yes, reset my Plus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
